import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useLazyQuery } from '@apollo/client'
import { GET_LOCATION_LIST, GET_COUNTRY_LIST, GET_WATER_METRO_FROM_LOCATION, GET_WATER_METRO_TO_LOCATION } from 'graphql/common/queries'
import { GET_ROUTE_TYPE } from 'graphql/route/queries'
import { currentSessionDecoded, getAllowedRouteTypes } from 'helpers/auth-helper'

import { Button } from 'flowbite-react'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/material_blue.css'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { ChevronDownIcon, UsersIcon, ArrowsRightLeftIcon, MapPinIcon, ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline'

export function BookingSearchComponent({ onSearch, onClear }) {
    const { t } = useTranslation()
    const [boatLocationList, setBoatLocationList] = useState([])
    const [arrivalLocationList, setArrivalLocationList] = useState([])
    const [fromLocation, setFromLocation] = useState('')
    const [toLocation, setToLocation] = useState('')
    const [fromLocationId, setFromLocationId] = useState(null)
    const [toLocationId, setToLocationId] = useState(null)
    const [isFromListVisible, setIsFromListVisible] = useState(false)
    const [isToListVisible, setIsToListVisible] = useState(false)
    const [searchDate, setSearchDate] = useState('')
    const [returnDate, setReturnDate] = useState('')
    const [isValidationError, setIsValidationError] = useState(false)
    const [validationMessage, setValidationMessage] = useState('Please fill all the fields')
    const [selectedPersonCount, setSelectedPersonCount] = useState(1)
    const [selectedPerson, setSelectedPerson] = useState({
        ADL: 1,
        CHD: 0,
        INF: 0,
        SNR: 0,
    })
    const [showClearAll, setShowClearAll] = useState(false)
    const [routeTypeList, setRouteTypeList] = useState([])
    const [countryList, setCountryList] = useState([])
    const [selectedRouteType, setSelectedRouteType] = useState('')
    const [selectedCountry, setSelectedCountry] = useState('BH') // Default to Bahrain
    const [selectedTripType, setSelectedTripType] = useState('one-way') // Default to one-way
    const [isInitialLoading, setIsInitialLoading] = useState(true)
    const [sessionData, setSessionData] = useState(null)
    const [allowedRouteType, setAllowedRouteType] = useState('ALL') // 'ALL', 'INL', or 'DMC'

    const [getFromLocationList, { loading: locationListLoading, error: locationListError }] = useLazyQuery(
        GET_WATER_METRO_FROM_LOCATION,
        {
            context: { unauth: true },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.waterMetroFromLocation?.locations?.length > 0) {
                    setBoatLocationList(data.waterMetroFromLocation.locations)
                } else {
                    setBoatLocationList([])
                }
            },
        }
    )

    const [getToLocationList, { loading: toLocationListLoading, error: toLocationListError }] = useLazyQuery(
        GET_WATER_METRO_TO_LOCATION,
        {
            context: { unauth: true },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.waterMetroToLocation?.locations?.length > 0) {
                    setArrivalLocationList(data.waterMetroToLocation.locations)
                } else {
                    setArrivalLocationList([])
                }
            },
        }
    )

    const [getRouteTypeList, { loading: routeTypeListLoading, error: routeTypeListError }] = useLazyQuery(
        GET_ROUTE_TYPE,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.routeType?.length > 0) {
                    setRouteTypeList(data.routeType)
                }
            },
        }
    )

    const [getCountryList, { loading: countryListLoading, error: countryListError }] = useLazyQuery(
        GET_COUNTRY_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.country?.length > 0) {
                    setCountryList(data.country)
                }
            },
        }
    )

    const getRouteTypes = () => {
        getRouteTypeList({
            variables: {
                code: null,
            },
        })
    }

    const getCountries = () => {
        getCountryList({
            variables: {
                code: null,
            },
        })
    }

    const getFilteredLocations = () => {
        const locationFilter = {
            route_type: selectedRouteType,
            country_code: selectedRouteType === 'DMC' ? selectedCountry : ''
        }

        getFromLocationList({
            variables: {
                locationFilter
            }
        })
    }

    const getArrivalLocations = (fromLocationId) => {
        if (!fromLocationId || !selectedRouteType) {
            setArrivalLocationList([])
            return
        }

        const routeType = selectedRouteType === 'DMC' ? 'DMC' : 'INL'

        getToLocationList({
            variables: {
                fromLocationId,
                routeType
            }
        })
    }

    // Fetch session data and determine allowed route types on mount
    useEffect(() => {
        const fetchSessionData = async () => {
            try {
                const session = await currentSessionDecoded()
                if (session) {
                    setSessionData(session)
                    const allowed = getAllowedRouteTypes(session)
                    setAllowedRouteType(allowed)
                }
            } catch (error) {
                console.error('Error fetching session:', error)
            }
        }
        fetchSessionData()
        getRouteTypes()
        getCountries()
    }, [])

    useEffect(() => {
        // Check if initial data loading is complete
        if (!routeTypeListLoading && !countryListLoading && routeTypeList.length > 0 && countryList.length > 0) {
            setIsInitialLoading(false)
        }
    }, [routeTypeListLoading, countryListLoading, routeTypeList.length, countryList.length])

    // Filter route types based on user role
    const getFilteredRouteTypes = () => {
        if (!routeTypeList || routeTypeList.length === 0) return []
        
        if (allowedRouteType === 'ALL') {
            return routeTypeList
        }
        
        // Filter to only show allowed route type
        return routeTypeList.filter(type => type.code === allowedRouteType)
    }

    const filteredRouteTypes = getFilteredRouteTypes()

    useEffect(() => {
        if (selectedRouteType) {
            getFilteredLocations()
        } else {
            setBoatLocationList([])
        }
    }, [selectedRouteType, selectedCountry])

    const handleSwap = () => {
        setFromLocation(toLocation)
        setFromLocationId(toLocationId)
        setToLocation(fromLocation)
        setToLocationId(fromLocationId)
    }

    const handleChange = (setter, setVisibility) => event => {
        setter(event.target.value)
        setVisibility(true)
    }

    const validateSearch = () => {
        if (selectedPersonCount === 0) {
            setValidationMessage(t('Please select number of passengers'))
            setIsValidationError(true)
            return
        }
        if (!selectedRouteType) {
            setValidationMessage(t('Please select route type'))
            setIsValidationError(true)
            return
        }
        if (selectedRouteType === 'DMC' && !selectedCountry) {
            setValidationMessage(t('Please select country for domestic route'))
            setIsValidationError(true)
            return
        }
        if (!fromLocationId || !toLocationId || !searchDate) {
            setValidationMessage('Please enter all details')
            setIsValidationError(true)
            return
        }
        if (selectedTripType === 'round-trip' && !returnDate) {
            setValidationMessage(t('Please select return date for round trip'))
            setIsValidationError(true)
            return
        }
        if (selectedTripType === 'round-trip' && returnDate && searchDate && returnDate < searchDate) {
            setValidationMessage(t('Return date cannot be before departure date'))
            setIsValidationError(true)
            return
        }
        setIsValidationError(false)
        onSearch({
            fromLocationId,
            toLocationId,
            searchDate,
            returnDate: selectedTripType === 'round-trip' ? returnDate : null,
            tripType: selectedTripType,
            routeType: selectedRouteType,
            country: selectedCountry,
            passengers: {
                ADL: selectedPerson.ADL,
                CHD: selectedPerson.CHD,
                INF: selectedPerson.INF,
                SNR: selectedPerson.SNR,
            },
        })
        setShowClearAll(true)
    }

    const handleClearAll = () => {
        setFromLocation('')
        setToLocation('')
        setFromLocationId(null)
        setToLocationId(null)
        setSearchDate('')
        setReturnDate('')
        setSelectedPersonCount(1)
        setSelectedPerson({
            ADL: 1,
            CHD: 0,
            INF: 0,
            SNR: 0,
        })
        setSelectedRouteType('')
        setSelectedCountry('BH')
        setSelectedTripType('one-way')
        setBoatLocationList([])
        setArrivalLocationList([])
        setIsValidationError(false)
        setShowClearAll(false)
        onClear()
    }

    const handleSelectPerson = (option, action) => {
        if (action === 'INC') {
            setSelectedPerson({ ...selectedPerson, [option]: selectedPerson[option] + 1 })
            setSelectedPersonCount(selectedPersonCount + 1)
        } else if (action === 'DEC' && selectedPerson[option] > 0) {
            setSelectedPerson({ ...selectedPerson, [option]: selectedPerson[option] - 1 })
            setSelectedPersonCount(selectedPersonCount - 1)
        }
        setIsValidationError(false)
    }

    const renderAutocomplete = (placeholder, inputValue, setInputValue, setInputId, isListVisible, setListVisible, locationList = boatLocationList, isArrival = false) => (
        <div className="relative w-full">
            <input
                type="text"
                value={inputValue}
                onChange={handleChange(setInputValue, setListVisible)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900  placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-0 sm:text-sm sm:leading-6"
                placeholder={placeholder}
                disabled={!selectedRouteType}
            />
            {isListVisible && inputValue && locationList.length > 0 && (
                <ul className="absolute bg-white border border-gray-300 mt-1 max-h-40 overflow-y-auto w-full z-50">
                    {locationList
                        .filter(location => location.name.en.toLowerCase().includes(inputValue.toLowerCase()))
                        .map(location => (
                            <button
                                key={location.id}
                                className="p-2 cursor-pointer hover:bg-gray-200 w-full text-left"
                                onClick={() => {
                                    setInputValue(location.name.en)
                                    setInputId(location.id)
                                    setListVisible(false)
                                    setIsValidationError(false)
                                    
                                    // If this is departure location selection, fetch arrival locations
                                    if (!isArrival && fromLocationId !== location.id) {
                                        getArrivalLocations(location.id)
                                        // Clear arrival location when departure changes
                                        setToLocation('')
                                        setToLocationId(null)
                                    }
                                }}
                                onKeyPress={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setInputValue(location.name.en)
                                        setInputId(location.id)
                                        setListVisible(false)
                                        setIsValidationError(false)
                                        
                                        // If this is departure location selection, fetch arrival locations
                                        if (!isArrival && fromLocationId !== location.id) {
                                            getArrivalLocations(location.id)
                                            // Clear arrival location when departure changes
                                            setToLocation('')
                                            setToLocationId(null)
                                        }
                                    }
                                }}
                            >
                                {location.name.en}
                            </button>
                        ))}
                </ul>
            )}
        </div>
    )

    return (
        <>
            {isInitialLoading && (
                <div className="flex justify-center items-center bg-white rounded-lg border border-gray-200 shadow-sm py-8 px-6 mb-4">
                    <div className="flex flex-col items-center space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                </div>
            )}
            
            {!isInitialLoading && (
                <>
                    {!routeTypeListLoading && !routeTypeListError && filteredRouteTypes?.length === 0 && (
                        <div className="flex justify-center items-center bg-white py-5">
                            <div className="text-lg text-center">{t('No route types found!')}</div>
                        </div>
                    )}
                    
                    <div className="bg-white rounded-lg p-0">
                        {/* First Row - Dropdowns */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            {/* Route Type Dropdown */}
                            <div className="w-full">
                                <select
                                    value={selectedRouteType}
                                    onChange={(e) => {
                                        setSelectedRouteType(e.target.value)
                                        setSelectedCountry(e.target.value === 'DMC' ? 'BH' : '')
                                        setFromLocation('')
                                        setToLocation('')
                                        setFromLocationId(null)
                                        setToLocationId(null)
                                        setReturnDate('')
                                        setArrivalLocationList([])
                                        setIsValidationError(false)
                                    }}
                                    className="block w-full rounded-md border-0 py-3 sm:py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6 min-h-[44px] sm:min-h-auto"
                                >
                                    <option value="">{t('Route Type')}</option>
                                    {filteredRouteTypes?.map((routeType) => (
                                        <option key={routeType.code} value={routeType.code}>
                                            {routeType.description.en}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Country Dropdown - Only show when Route Type is Domestic */}
                            {selectedRouteType === 'DMC' && (
                                <div className="w-full">
                                    <select
                                        value={selectedCountry}
                                        onChange={(e) => {
                                            setSelectedCountry(e.target.value)
                                            setFromLocation('')
                                            setToLocation('')
                                            setFromLocationId(null)
                                            setToLocationId(null)
                                            setReturnDate('')
                                            setArrivalLocationList([])
                                            setIsValidationError(false)
                                        }}
                                        className="block w-full rounded-md border-0 py-3 sm:py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6 min-h-[44px] sm:min-h-auto"
                                    >
                                        <option value="">{t('Country')}</option>
                                        {countryList?.map((country) => (
                                            <option key={country.alpha_iso2} value={country.alpha_iso2}>
                                                {country.country}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Trip Type Dropdown */}
                            <div className="w-full">
                                <select
                                    value={selectedTripType}
                                    onChange={(e) => {
                                        setSelectedTripType(e.target.value)
                                        if (e.target.value === 'one-way') {
                                            setReturnDate('')
                                        }
                                        setIsValidationError(false)
                                    }}
                                    className="block w-full rounded-md border-0 py-3 sm:py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary text-sm sm:text-sm sm:leading-6 min-h-[44px] sm:min-h-auto"
                                >
                                    <option value="one-way">{t('One Way')}</option>
                                    <option value="round-trip">{t('Round Trip')}</option>
                                </select>
                            </div>

                            {/* Passengers Dropdown */}
                            <div className="w-full">
                                <Popover className="relative">
                                    <PopoverButton className="w-full inline-flex items-center justify-between px-3 py-3 sm:py-2.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary min-h-[44px] sm:min-h-auto">
                                        <div className="flex items-center gap-2">
                                            <UsersIcon className="h-5 w-5 text-gray-500" />
                                            <span className="truncate">
                                                {selectedPersonCount > 0
                                                    ? selectedPersonCount + ' ' + t('Passengers')
                                                    : t('Select Passenger')}
                                            </span>
                                        </div>
                                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                                    </PopoverButton>

                                    <PopoverPanel
                                        transition
                                        className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-min -translate-x-1/2 px-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                                    >
                                        <div className="w-56 shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6 text-gray-900 shadow-lg ring-1 ring-gray-900/5">
                                            <div
                                                className="py-1"
                                                role="menu"
                                                aria-orientation="vertical"
                                                aria-labelledby="menu-button"
                                            >
                                                {/* Adult Selection */}
                                                <div className="flex justify-between items-center px-4 py-2">
                                                    <span className="text-sm text-gray-700">{t('Adults')}</span>
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => handleSelectPerson('ADL', 'DEC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="px-3">{selectedPerson.ADL}</span>
                                                        <button
                                                            onClick={() => handleSelectPerson('ADL', 'INC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Child Selection */}
                                                <div className="flex justify-between items-center px-4 py-2">
                                                    <span className="text-sm text-gray-700">{t('Children')}</span>
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => handleSelectPerson('CHD', 'DEC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="px-3">{selectedPerson.CHD}</span>
                                                        <button
                                                            onClick={() => handleSelectPerson('CHD', 'INC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Infant Selection */}
                                                <div className="flex justify-between items-center px-4 py-2">
                                                    <span className="text-sm text-gray-700">{t('Infants')}</span>
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => handleSelectPerson('INF', 'DEC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="px-3">{selectedPerson.INF}</span>
                                                        <button
                                                            onClick={() => handleSelectPerson('INF', 'INC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                                {/* Senior Selection */}
                                                <div className="flex justify-between items-center px-4 py-2">
                                                    <span className="text-sm text-gray-700">{t('Seniors')}</span>
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => handleSelectPerson('SNR', 'DEC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            -
                                                        </button>
                                                        <span className="px-3">{selectedPerson.SNR}</span>
                                                        <button
                                                            onClick={() => handleSelectPerson('SNR', 'INC')}
                                                            className="px-3 py-2 sm:px-2 sm:py-1 bg-gray-200 rounded text-gray-700 min-h-[36px] sm:min-h-auto"
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </PopoverPanel>
                                </Popover>
                            </div>
                        </div>

                        {/* Second Row - Full Width Location Container */}
                        <div className="mb-4">
                            <div className="flex flex-col sm:flex-row items-center gap-3 p-1.5 border border-gray-300 rounded-lg">
                                {/* From Location */}
                                <div className="flex items-center w-full sm:w-1/2">
                                    <MapPinIcon className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                                    {renderAutocomplete(
                                        selectedRouteType ? t('Enter Departure') : t('Select route type first'),
                                        fromLocation,
                                        setFromLocation,
                                        setFromLocationId,
                                        isFromListVisible,
                                        setIsFromListVisible
                                    )}
                                </div>

                                {/* Switch Icon */}
                                <div className="flex justify-center p-1.5 border border-primary rounded-full cursor-pointer hover:bg-gray-50 transition-colors">
                                    <ArrowsRightLeftIcon
                                        onClick={handleSwap}
                                        className="h-5 w-5 text-primary"
                                    />
                                </div>

                                {/* To Location */}
                                <div className="flex items-center w-full sm:w-1/2">
                                    <MapPinIcon className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                                    {renderAutocomplete(
                                        selectedRouteType ? t('Enter Arrival') : t('Select route type first'),
                                        toLocation,
                                        setToLocation,
                                        setToLocationId,
                                        isToListVisible,
                                        setIsToListVisible,
                                        arrivalLocationList,
                                        true
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Third Row - Date and Search */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {/* Departure Date */}
                            <div className="w-full">
                                <div className="border border-gray-300 rounded-lg overflow-hidden">
                                    <Flatpickr
                                        value={searchDate}
                                        placeholder={t('Departure Date')}
                                        onChange={date => {
                                            setSearchDate(
                                                date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : ''
                                            )
                                            setIsValidationError(false)
                                        }}
                                        options={{
                                            altInput: true,
                                            altFormat: 'Y/m/d',
                                            dateFormat: 'Y/m/d',
                                            minDate: 'today',
                                            maxDate: new Date().fp_incr(30),
                                            defaultDate: 'today',
                                            locale: {
                                                firstDayOfWeek: 1,
                                                weekdays: {
                                                    shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                                                    longhand: [
                                                        'Sunday',
                                                        'Monday',
                                                        'Tuesday',
                                                        'Wednesday',
                                                        'Thursday',
                                                        'Friday',
                                                        'Saturday',
                                                    ],
                                                },
                                                months: {
                                                    shorthand: [
                                                        'Jan',
                                                        'Feb',
                                                        'Mar',
                                                        'Apr',
                                                        'May',
                                                        'Jun',
                                                        'Jul',
                                                        'Aug',
                                                        'Sep',
                                                        'Oct',
                                                        'Nov',
                                                        'Dec',
                                                    ],
                                                    longhand: [
                                                        'January',
                                                        'February',
                                                        'March',
                                                        'April',
                                                        'May',
                                                        'June',
                                                        'July',
                                                        'August',
                                                        'September',
                                                        'October',
                                                        'November',
                                                        'December',
                                                    ],
                                                },
                                            },
                                        }}
                                        className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-0 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            {/* Return Date - Only show when Round Trip is selected */}
                            {selectedTripType === 'round-trip' && (
                                <div className="w-full">
                                    <div className="border border-gray-300 rounded-lg overflow-hidden">
                                        <Flatpickr
                                            value={returnDate}
                                            placeholder={t('Return Date')}
                                            onChange={date => {
                                                setReturnDate(
                                                    date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : ''
                                                )
                                                setIsValidationError(false)
                                            }}
                                            options={{
                                                altInput: true,
                                                altFormat: 'Y/m/d',
                                                dateFormat: 'Y/m/d',
                                                minDate: searchDate || 'today',
                                                maxDate: new Date().fp_incr(30),
                                                locale: {
                                                    firstDayOfWeek: 1,
                                                    weekdays: {
                                                        shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                                                        longhand: [
                                                            'Sunday',
                                                            'Monday',
                                                            'Tuesday',
                                                            'Wednesday',
                                                            'Thursday',
                                                            'Friday',
                                                            'Saturday',
                                                        ],
                                                    },
                                                    months: {
                                                        shorthand: [
                                                            'Jan',
                                                            'Feb',
                                                            'Mar',
                                                            'Apr',
                                                            'May',
                                                            'Jun',
                                                            'Jul',
                                                            'Aug',
                                                            'Sep',
                                                            'Oct',
                                                            'Nov',
                                                            'Dec',
                                                        ],
                                                        longhand: [
                                                            'January',
                                                            'February',
                                                            'March',
                                                            'April',
                                                            'May',
                                                            'June',
                                                            'July',
                                                            'August',
                                                            'September',
                                                            'October',
                                                            'November',
                                                            'December',
                                                        ],
                                                    },
                                                },
                                            }}
                                            className="block w-full rounded-md border-0 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-0 sm:text-sm sm:leading-6"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Search Button and Clear All */}
                            <div className="flex flex-col gap-2">
                                <Button
                                    className="flex hover:bg-primary/[.9] w-full text-center bg-primary focus-visible:outline-primary justify-center rounded-md px-6 py-3 sm:py-1 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 min-h-[44px] sm:min-h-auto"
                                    onClick={() => validateSearch()}
                                    disabled={!selectedRouteType}
                                >
                                    {t('Search')}
                                </Button>
                                {showClearAll && (
                                    <div className="flex justify-end">
                                        <button
                                            className='text-red hover:text-red/80 flex items-center justify-center gap-1 text-sm font-medium underline transition-colors'
                                            onClick={handleClearAll}
                                        >
                                            <ArchiveBoxXMarkIcon className='w-4 h-4' /> {t('Clear All')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Validation Messages */}
                    {isValidationError && <p className="text-red text-sm mt-2">{t(validationMessage)}</p>}
                    {selectedRouteType && boatLocationList.length === 0 && !locationListLoading && (
                        <p className="text-red text-sm mt-2">{t('No departure locations found for selected route type')}</p>
                    )}
                    {fromLocationId && arrivalLocationList.length === 0 && !toLocationListLoading && (
                        <p className="text-red text-sm mt-2">{t('No arrival locations found for selected departure')}</p>
                    )}
                </>
            )}
        </>
    )
}
